import type { Dict } from "../core.js";
import type { partnerSets as vi } from "../vi/partnerSets.js";

export const partnerSets: Dict<typeof vi> = {
  setsTitle: "Credential sets",
  setsIntro: "Each environment can hold several named credential sets. A set must PASS a test run before it can be bound and enabled for an app.",
  noSets: "No sets in this environment yet.",
  addSet: "Add set",
  name: "Set name",
  namePlaceholder: "e.g. « Main UAT account »",
  edit: "Edit",
  cancel: "Cancel",
  save: "Save",
  saving: "Saving…",
  create: "Create set",
  delete: "Delete",
  confirmDelete: "Delete set « {name} »?",
  deleted: "Deleted.",
  saved: "Saved. The set must be tested again before it can be enabled for another app.",
  runTest: "Run test",
  testing: "Testing…",
  merchantIdOptional: "Merchant ID (optional, for the VA list step)",
  openLab: "Lab",
  openAgency: "Agency",
  sendTest: "Send a real test email →",
  sendTestTitle: "Opens the Mail page: every app with this set enabled has a « Send test » button — the app's own server sends a real email through this set.",
  sendTestNeedsRoute: "Not a route of any app yet — choose it on the app's Mail page (through the gate)",
  routeOf: "Route of:",
  "routeRole.primary": "primary",
  "routeRole.standby": "standby",
  "routeRole.disabled": "disabled",
  manageRoutesAt: "Choose routes on Mail · {app}",
  migrated: "migrated from the old settings",
  boundTo: "Bound to: {apps}",
  notBound: "Not bound to any app",
  lastTest: "Last test {when} · {by}",
  neverTested: "Never tested",
  "status.untested": "Untested",
  "status.passed": "Passed",
  "status.failed": "Failed",
  "level.auth": "key authentication",
  "level.reachability": "server reachable only — keys NOT verified",
  "level.suite": "non-destructive suite",
  "step.ok": "ok",
  "step.failed": "failed",
  "step.unknown": "unknown",
  "step.skipped": "skipped",
  showReport: "Show report",
  hideReport: "Hide report",
  bindingsTitle: "App bindings",
  bindingsIntro: "Which set each app uses in this environment. The switch only unlocks once the set has PASSED a test run.",
  app: "App",
  set: "Set",
  enabled: "On",
  chooseSet: "— choose a set —",
  grandfathered: "Enabled from before — this set was NEVER tested. Run a test.",
  previousUntil: "The previous set « {name} » is still sent along (only to verify inbound pushes) until {when}.",
  needsPassed: "Run a passing test on this set before enabling it.",
  switchNeedsPassed: "The app is ON — only switch to a set that has PASSED a test (or turn it off first).",
  confirmDisable: "Turn off {app} ({env})?",
  alwaysOn: "Always on",
  alwaysOnTitle: "Sandbox is always on for every app — there is no switch. Only production (live) waits for the hub to enable it.",
  bindingsIntroSandbox: "Which set each app uses in sandbox. Sandbox is ALWAYS ON — without a bound set the app uses the catalog's public values (base URL…); a set only needs its required fields, no test run.",
  catalogFallback: "Not bound — using catalog values.",
  bindingSaved: "Binding updated.",
  failed: "Could not complete the action.",
  credentialsChangedWarning: "This set is ON for an app — changing keys applies immediately and resets the set to « untested ».",
  hubOnly: "hub only",
  hubOnlyHint: "The hub keeps this field to read the provider account — it is NEVER sent to apps. Changing only this field does not change the delivered configuration and needs no new test run.",
  versionPair: "Credential set v{n} · Delivered config v{delivery}",
  hubOnlyEditNotice: "Only « hub only » fields change: apps receive nothing new — no grace for the old key, no config push, the set keeps its current test result.",
  deliveryVersionHint: "« Delivered config » is what apps receive (every field except « hub only ») — it only goes up when that part changes. Editing a management key only bumps the set version.",
  pricingTitle: "How the partner charges Bube",
  pricingIntro: "Pick the pricing model and enter the prices of this set's contract. To change prices add a new entry with an effective date; entries already in effect are history and cannot be deleted.",
  pricingNone: "No pricing yet — the app assumes per-transaction (pausing still stops at the partner).",
  pricingCurrent: "in effect",
  pricingFuture: "from {date}",
  pricingFrom: "Effective from",
  pricingModel: "Pricing model",
  pricingScope: "Charged per",
  pricingUnitPrice: "Price per transaction (VND)",
  pricingFree: "Free transactions per month",
  pricingMonthlyFee: "Monthly fee (VND)",
  pricingIncluded: "Transactions included",
  pricingOverage: "Price per extra transaction (VND, empty = partner stops notifying)",
  pricingAdd: "Add price",
  pricingAdded: "Price added.",
  pricingDelete: "Delete upcoming price",
  pricingInvalid: "Prices must be non-negative whole numbers (VND).",
  pricingSummaryTx: "{price}/transaction",
  pricingSummaryFree: ", {count} free/month",
  pricingSummaryMonthly: "{fee}/month",
  pricingSummaryCapped: "{fee}/month incl. {count} transactions, {over}/extra transaction",
  pricingSummaryCappedStop: "{fee}/month incl. {count} transactions, stops notifying beyond",
};
